import { ResizeObserver } from "@juggle/resize-observer";
import classnames from "classnames";
import { isEmpty } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import SVG from "react-inlinesvg";
import { InView } from "react-intersection-observer";
import { animated, useSpring, useTransition } from "react-spring";
import useMeasure from "react-use-measure";
import { CONTENT_GENERATION_FAILED, DESCRIBE_SENSES, isAutoPOV } from "../../src/shared/constants";
import { customFunctions } from "../../src/shared/customFunctions";
import Icon from "./Icon";
import Utils from "./Utils";
import CardView from "./cardset/CardView";
import FilterInput from "./cardset/FilterInput";
import ImageCardView from "./cardset/ImageCardView";
import { Chiclet } from "./components/Chiclet";
import { getStyleChicletLabel } from "./utils/getStyleChicletLabel";
import { getAirTableSupportUrl } from "./components/HelpMenu";
import CardSetError from "./cardset/CardSetError";
import { TourSignal } from "./components/StickyTour/TourTypes";
import { CONTENT_GENERATION_FAILED_MESSAGE, CONTENT_POLICY_MESSAGE, ContentViolationError } from "./lib/Errors";
import { getErrorString, getSprintErrorMessage, isProbableModelOutageError } from "./lib/modelOutage";
import { useCrossAppSignalStore } from "./stores/crossAppSignalStore";
import { useStickyTourStore } from "./stores/stickyTourStore";
import { useStoryBibleStore } from "./stores/storyBibleStore";
import { useSudowriteStore } from "./stores/sudowriteStore";

export default function CardSet(props) {
  // prompt, promptPresenter, content, contentPresenter, completionId, key, onVisible

  const [truncatePrompt, setTruncatePrompt] = useState(true);
  const [card, setCard] = useState(props.model);
  const [loading, setLoading] = useState(props.loading);
  const [hidden, setHidden] = useState(props.hidden);
  const [doneStreaming, setDoneStreaming] = useState(props.doneStreaming);
  const [error, setError] = useState(props.error);
  const [ready, setReady] = useState(props.ready); // signal to cardset to start streaming
  const [demo] = useState(props.demo);
  const [cardContent, setCardContent] = useState("");
  const [cardChiclets, setCardChiclets] = useState(props.model?.data?.chiclets || {});

  const elementRefs = useStoryBibleStore((state) => state.elementRefs);
  const isActive = useStoryBibleStore((state) => state.isActive);
  const setIsActive = useStoryBibleStore((state) => state.setIsActive);

  const [params, setParams] = useState(props.params);
  const [alreadyStreamed, setAlreadyStreamed] = useState(props.doneStreaming);

  // Sync loading / doneStreaming / hidden / card from props when the parent
  // drives the stream lifecycle externally (SW-9212 Draft placeholder cards).
  // Wormhole-style cards don't update these props after mount, so this is a
  // no-op for them. cardContent is bumped so the content map re-renders even
  // when only card.data.content is mutated (same model reference).
  useEffect(() => {
    setLoading(props.loading);
  }, [props.loading]);
  useEffect(() => {
    setDoneStreaming(props.doneStreaming);
    if (props.doneStreaming) setAlreadyStreamed(true);
  }, [props.doneStreaming]);
  useEffect(() => {
    setHidden(props.hidden);
  }, [props.hidden]);
  useEffect(() => {
    if (props.model) setCard(props.model);
  }, [props.model]);
  useEffect(() => {
    setError(props.error);
  }, [props.error]);

  const editorOptions = useSudowriteStore((state) => state.editorOptions);
  const subscription = useSudowriteStore((state) => state.subscription);
  const userData = useSudowriteStore((state) => state.userData);
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const setCrossAppSignal = useCrossAppSignalStore((state) => state.setSignalActive);

  // Use ResizeObserver to detect when the outline ref has finished loading
  useEffect(() => {
    // Only start observing when the story bible is active and the outline ref exists
    if (!isActive || !elementRefs?.outline?.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          // Wait a bit more to ensure the element is fully rendered and stable
          setTimeout(() => {
            resizeObserver.disconnect();
          }, 300);
          break;
        }
      }
    });

    resizeObserver.observe(elementRefs.outline.current);

    return () => resizeObserver.disconnect();
  }, [isActive, elementRefs?.outline]);

  // Helper function to reliably find and scroll to the outline section
  const scrollToOutline = useCallback(() => {
    // First try the ref if it's available
    if (elementRefs?.outline?.current) {
      elementRefs.outline.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
      return;
    }

    // Fallback: search for the outline section by looking for specific content
    const storyBibleContainer = document.querySelector(".story-bible");
    if (storyBibleContainer) {
      // Look for the outline section - it should contain StructuredOutline component
      const outlineSection = storyBibleContainer.querySelector('.section:has([data-testid="structured-outline"]), .section:has(.structured-outline)');
      if (outlineSection) {
        outlineSection.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
        return;
      }

      // Alternative: look for sections and find the one that's likely the outline
      const sections = storyBibleContainer.querySelectorAll(".section");
      if (sections.length > 0) {
        // The outline is typically the last major section before any bottom elements
        const outlineSection = sections[sections.length - 2] || sections[sections.length - 1];
        if (outlineSection) {
          outlineSection.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        }
      }
    }
  }, [elementRefs?.outline]);

  // Emits a view event for the cardset when the cardset is done streaming or opened
  useEffect(() => {
    if (!loading && !hidden && doneStreaming && props.model.fetched) {
      // console.log(`${props.model.data.source} ${props.model.id} card viewed`);
      props.model.emitViewEvents();
    }
  }, [loading, hidden, props.model.fetched, doneStreaming]);

  // measure the height of the cards container
  const [ref, { height }] = useMeasure({ polyfill: ResizeObserver });
  const [cardsHeight, setCardsHeight] = useState(0);

  // gets the cards list height for animation
  useEffect(() => {
    // When the tab isn't showing, then don't set the card height
    // this avoids seeing a pop-in animation when tabbing from favorites back to history
    if (props.tabShowing) {
      //Sets initial height
      setCardsHeight(height);
    }
  }, [height]);

  let showCards = !loading && !hidden;
  const config = {
    mass: 0.5,
    tension: 450,
  };

  // Animation to expand and collapse the height of the card container
  const expand = useSpring({
    config: {
      mass: 0.5,
      tension: 650,
    },
    height: showCards ? cardsHeight : 0,
  });

  // transition the first card slower than the rest to make it appear like the cards spring into place
  // TODO: probably don't need transitions to do this, instead just use a spring with cardsShow as the boolean like we do with expand
  const firstCardTransitions = useTransition(showCards, null, {
    from: { opacity: 0, transform: "translate(0,-10%)" },
    enter: { opacity: 1, transform: "translate(0,0)" },
    leave: { opacity: 0, transform: "translate(0,-10%)" },
    config: config,
  });

  const cardTransitions = useTransition(showCards, null, {
    from: { opacity: 0, transform: "translate(0,-40%)" },
    enter: { opacity: 1, transform: "translate(0,0)" },
    leave: { opacity: 0, transform: "translate(0,-540%)" }, // we animated it up more than the first card to avoid affecting later card sets when the cards that are disappearing cause relayouting
    config: config,
  });

  let content, prompt, title;
  let count = 0;
  // let card = props.model;

  // Effect to start streaming from the AI function
  useEffect(() => {
    if (ready && !doneStreaming) {
      let url;
      // console.log("params", params);

      if (card.data.custom) {
        // all custom functions go through the custom endpoint
        url = "/api/ai/custom";
      } else if (card.data.source === "plugin") {
        url = params.endpointOverride;
        delete params.endpointOverride;
      } else if (card.data.source === "rewrite") {
        // Fugly alert: this is a special case where rewrites must pull from editorOptions to get
        // the correct number of outputcards. This is because rewrite cards are initiated from
        // the card itself and not from the button. But if we persisted outputCards on the
        // the card params, then it might be stale by the time the invocation is started
        // so we instead pull from userData here
        url = `/api/ai/${card.data.source}`;
        params.outputCards = editorOptions.rewriteOutputCards;
        params.preceding_text = card.data.preceding_text;
        params.following_text = card.data.following_text;
      } else if (card.data.source === "myVoiceRewrite") {
        params.outputCards = editorOptions.rewriteOutputCards;
        params.writingSample = userData?.data?.userStyleExamples;
        params.creativitySliderPosition = editorOptions.creativitySliderPosition;
        url = `/api/ai/${card.data.source}`;
      } else {
        // sources match to the url endpoint
        url = `/api/ai/${card.data.source}`;
      }

      // DEMO FAKE STREAMING OF CARDS
      if (card.data.demo && card.data.demoCardContent) {
        card.demoStream(
          card.data.demoCardContent,
          params,
          (cardModel) => {
            setLoading(cardModel.isEmpty());
            setCard(cardModel);
          },
          () => {
            setDoneStreaming(true);
            props.onStreamSettled?.(props.model);
            if (card.data.onDone) {
              card.data.onDone();
            }
          }
        );
      } else {
        let p = params;

        if (card.id && card.fetched) {
          // send up the card id so that the backend can decide whether to overwrite the card
          p.cardId = card.id;
        }

        if (card.data.custom) {
          p.source = card.data.source;
        }

        // console.log(p);
        if (card.data.crossAppSignal) {
          setCrossAppSignal(card.data.crossAppSignal, true);
        }
        // Do not return the stream Promise — React treats effect return values as
        // sync cleanups. Returning a Promise crashes on unmount (SUDOWRITE-REACT-X56).
        // Parent AbortController (props.signal) already aborts in-flight streams on
        // document switch / Multiverse teardown.
        let streamSettled = false;
        card.streamWithIdToken(
          url,
          p,
          props.signal,
          1000,
          (cardModel) => {
            if (!alreadyStreamed && !streamSettled) {
              // If we've streamed the card already, then never go back to the loading state
              setLoading(cardModel.isEmpty());
            }
            setCard(cardModel);
            // This value gets set to trigger a re-render, since cardModel changes
            // aren't always detected by React

            // The text that is being concatenated on may be on the .text or top level
            // here we detect which it is, testing the .text first, then top level
            const text = cardModel?.data?.content?.[0]?.text || cardModel?.data?.content?.[0];

            // set it to cardContent, even though cardContent is not used, this will force a re-render
            setCardContent(text);

            // Track chiclets in state to force re-render when they arrive during streaming
            if (cardModel?.data?.chiclets) {
              setCardChiclets({ ...cardModel.data.chiclets });
            }
          },
          (error) => {
            streamSettled = true;
            setDoneStreaming(true);
            setAlreadyStreamed(true);
            setLoading(false);

            let displayError;
            if (error?.cause instanceof ContentViolationError) {
              if (error.message === CONTENT_GENERATION_FAILED) {
                displayError = { message: CONTENT_GENERATION_FAILED_MESSAGE };
              } else {
                displayError = { message: CONTENT_POLICY_MESSAGE };
              }
            } else if (error?.message) {
              displayError = error;
            } else if (typeof error === "string") {
              displayError = { message: error };
            } else {
              displayError = { message: "An unknown error occurred. Please try your request again." };
            }
            setError(displayError);
            props.onStreamSettled?.(props.model, displayError);

            if (card.data.crossAppSignal) {
              setCrossAppSignal(card.data.crossAppSignal, false);
            }
          },
          () => {
            streamSettled = true;
            setDoneStreaming(true);
            setAlreadyStreamed(true);
            setLoading(false);
            props.onStreamSettled?.(props.model);
            sendFlowSignal(TourSignal.SET_DONE_STREAMING);
            if (card.data.crossAppSignal) {
              setCrossAppSignal(card.data.crossAppSignal, false);
            }
          }
        );
      }
    }
  }, [ready, params]);

  const isContentDeleted = useMemo(() => card.isContentDeleted.bind(card), [card]);

  const deleteCardItemAtPath = useCallback(
    async (deletePath) => {
      try {
        await card.deleteAtPath(deletePath);
      } catch (error) {
        console.error("Failed to delete card item", { cardId: card.id, fetched: card.fetched, deletePath }, error);
        setError({ message: "Could not delete this card" });
        return;
      }

      let removeCard = false;
      if (!card.hasItems()) {
        removeCard = true;
      }

      props.onDeleteCardItem(card.id, removeCard);
    },
    [card, props]
  );

  const createCardItemDeleteHandler = useCallback(
    (content) => () => deleteCardItemAtPath(card.pathFromContent(content)),
    [card, deleteCardItemAtPath]
  );

  const createCardItemDeleteHandlerByIndex = useCallback(
    (pathKey, index) => () => deleteCardItemAtPath([pathKey, index]),
    [deleteCardItemAtPath]
  );

  if (!props.model) {
    throw "Must pass in card model";
  }

  let filter = (array) => {
    return array.filter((completion, i) => {
      return completion && completion.length > 0 && completion.replace(/\s/g, "").length > 0;
    });
  };

  const isDialogue = props.model.data.source == "dialogue";
  // Failed runs may never receive a saved card ID. The initial invocation ID
  // arrives before output and identifies the same activity in support tools.
  const activityId = [card.data.initialInvocationId, card.data.invocationId]
    .find((id) => typeof id === "string" && id.trim())?.trim();
  const supportFormUrl = useMemo(
    () => getAirTableSupportUrl(subscription, { activityId }),
    [subscription, activityId]
  );
  // Only prewritten messages and the server-owned sprint admission code may
  // replace the generic fallback. Keep provider/storage diagnostics hidden.
  const sprintErrorMessage = getSprintErrorMessage(error);
  let errorKind = "generic";
  if (error === CONTENT_POLICY_MESSAGE || error?.message === CONTENT_POLICY_MESSAGE) {
    errorKind = "content-policy";
  } else if (getErrorString(error) === CONTENT_GENERATION_FAILED_MESSAGE) {
    errorKind = "generation-failed";
  } else if (getErrorString(error) === "Could not delete this card") {
    errorKind = "delete-failed";
  } else if (sprintErrorMessage) {
    errorKind = "sprint";
  } else if (isProbableModelOutageError(error)) {
    errorKind = "model-outage";
  }
  const errorMessage = error ? <CardSetError kind={errorKind} sprintMessage={sprintErrorMessage} activityId={activityId} supportUrl={supportFormUrl} /> : null;

  if (isDialogue) {
    return null;
  }

  switch (card.data.source) {
    case "multiverse":
    case "guidedWormhole":
      title = (
        <span>
          <Icon
            height={25}
            width={20}
            source={card.data.source}
          />
          <p>Write</p>
        </span>
      );

      let guidance = null;
      if (params) {
        guidance = params.guidance;
      } else {
        guidance = card.data.guidance;
      }

      prompt = guidance || card.data.prompt; // only show guidance if there is one

      // never show empty cars
      content = filter(card.data.content).map((completion, i) => {
        if (isContentDeleted(completion)) return null;
        count++;
        return (
          <CardView
            // onRemove={onRemove}
            highlightInsert={props.highlightInsert}
            pathKey={null}
            pathIndex={i}
            card={card}
            favoritesByCards={props.favoritesByCards}
            key={count}
            favoritesStore={props.favoritesStore}
            content={completion}
            formatMarkdown={false} // these are legacy cards that don't support markdown
            insertEditor={props.insertEditor}
            editor={props.editor}
            doneStreaming={doneStreaming}
            onFavorite={props.onFavorite}
            onUnfavorite={props.onUnfavorite}
            onDeleteCardItem={createCardItemDeleteHandler(completion)}
          />
        );
      });

      break;
    case "plugin":
      title = (
        <span>
          <Icon
            width={20}
            height={25}
            top={0}
            source={card.data.modifier.toLowerCase()}
          />
          <p>{card.data.modifier}</p>
        </span>
      );

      prompt = card.data.prompt;

      // rewrite shows cards in reverse order
      content = card.data.content
        .slice()
        .reverse()
        .map((item, i) => {
          let realIndex = card.data.content.length - 1 - i; // the index needs to be reversed as well
          if (isContentDeleted(item)) return null;

          count++;
          return (
            <CardView
              // onRemove={onRemove}
              highlightInsert={props.highlightInsert}
              pathKey={null}
              pathIndex={realIndex}
              card={card}
              favoritesByCards={props.favoritesByCards}
              key={realIndex}
              favoritesStore={props.favoritesStore}
              content={item.text}
              title={item.title || null}
              formatMarkdown={true}
              insertEditor={props.insertEditor}
            editor={props.editor}
              doneStreaming={doneStreaming}
              onFavorite={props.onFavorite}
              onUnfavorite={props.onUnfavorite}
              onDeleteCardItem={createCardItemDeleteHandler(item)}
              // compareOriginal={prompt}
            />
          );
        });

      // If this filter card set isn't on the top AND we haven't streamed cards in before, then we erase the card
      if (props.position !== 0 && content.length == 0) {
        return null;
      }

      break;
    case "rewrite":
      title = (
        <span>
          <Icon
            width={20}
            height={25}
            top={0}
            source={card.data.source}
          />
          <p>Rewrite</p>
        </span>
      );

      prompt = card.data.prompt;

      // rewrite shows cards in reverse order
      content = card.data.content
        .slice()
        .reverse()
        .map((item, i) => {
          let realIndex = card.data.content.length - 1 - i; // the index needs to be reversed as well
          if (isContentDeleted(item)) return null;

          count++;
          return (
            <CardView
              // onRemove={onRemove}
              highlightInsert={props.highlightInsert}
              pathKey={null}
              pathIndex={realIndex}
              card={card}
              favoritesByCards={props.favoritesByCards}
              key={realIndex}
              favoritesStore={props.favoritesStore}
              content={item.text}
              title={item.title || null}
              formatMarkdown={false} // these use a special diff format
              insertEditor={props.insertEditor}
            editor={props.editor}
              doneStreaming={doneStreaming}
              onFavorite={props.onFavorite}
              onUnfavorite={props.onUnfavorite}
              compareOriginal={prompt}
              onDeleteCardItem={createCardItemDeleteHandlerByIndex(null, realIndex)}
            />
          );
        });

      // If this filter card set isn't on the top AND we haven't streamed cards in before, then we erase the card
      if (props.position !== 0 && content.length == 0) {
        return null;
      }

      break;
    case "myVoiceRewrite":
      title = (
        <span>
          <Icon
            width={20}
            height={25}
            top={0}
            source={card.data.source}
          />
          <p>My Voice</p>
        </span>
      );

      prompt = card.data.prompt || card.data.passage || "";

      // rewrite shows cards in reverse order
      content = card.data.content
        .slice()
        .reverse()
        .map((item, i) => {
          let realIndex = card.data.content.length - 1 - i; // the index needs to be reversed as well

          count++;
          return (
            <CardView
              // onRemove={onRemove}
              highlightInsert={props.highlightInsert}
              pathKey={null}
              pathIndex={realIndex}
              card={card}
              favoritesByCards={props.favoritesByCards}
              key={realIndex}
              favoritesStore={props.favoritesStore}
              formatMarkdown={false} // these use a special diff format
              content={typeof item.text === "string" ? item.text : item}
              title={item.title || null}
              insertEditor={props.insertEditor}
            editor={props.editor}
              doneStreaming={doneStreaming}
              onFavorite={props.onFavorite}
              onUnfavorite={props.onUnfavorite}
              onDeleteCardItem={createCardItemDeleteHandler(item)}
            />
          );
        });

      // If this filter card set isn't on the top AND we haven't streamed cards in before, then we erase the card
      if (props.position !== 0 && content.length == 0) {
        return null;
      }
      break;
    case "poem":
      title = (
        <span>
          <Icon
            height={25}
            width={20}
            source={card.data.source}
          />
          <p>Poem</p>
        </span>
      );

      prompt = "Topic: " + card.data.prompt;

      // never show empty cars
      content = filter(card.data.content).map((completion, i) => {
        if (isContentDeleted(completion)) return null;

        count++;
        return (
          <CardView
            // onRemove={onRemove}
            highlightInsert={props.highlightInsert}
            pathKey={null}
            pathIndex={i}
            card={card}
            favoritesByCards={props.favoritesByCards}
            key={count}
            favoritesStore={props.favoritesStore}
            content={completion}
            formatMarkdown={false} // these use a special diff format
            insertEditor={props.insertEditor}
            editor={props.editor}
            doneStreaming={doneStreaming}
            onFavorite={props.onFavorite}
            onUnfavorite={props.onUnfavorite}
            onDeleteCardItem={createCardItemDeleteHandler(completion)}
          />
        );
      });

      break;
    case "whatIf":
      title = (
        <span>
          <Icon
            height={25}
            width={20}
            source={card.data.source}
          />
          <p>What If...</p>
        </span>
      );

      prompt = card.data.prompt;

      // never show empty cars
      content = filter(card.data.content).map((completion, i) => {
        if (isContentDeleted(completion)) return null;

        count++;

        // highlight the first sentence

        return (
          <CardView
            // onRemove={onRemove}
            boldFirstSentence={true}
            highlightInsert={props.highlightInsert}
            pathKey={null}
            pathIndex={i}
            card={card}
            favoritesByCards={props.favoritesByCards}
            key={count}
            favoritesStore={props.favoritesStore}
            content={completion}
            insertEditor={props.insertEditor}
            editor={props.editor}
            doneStreaming={doneStreaming}
            onFavorite={props.onFavorite}
            onUnfavorite={props.onUnfavorite}
            onDeleteCardItem={createCardItemDeleteHandler(completion)}
          />
        );
      });

      break;
    case "visualize":
      title = (
        <span>
          <Icon
            height={25}
            width={20}
            source={card.data.source}
          />
          <p>Visualize</p>
        </span>
      );

      prompt = card.data.prompt;

      // never show empty cards
      content = filter(card.data.content).map((completion, i) => {
        if (isContentDeleted(completion)) return null;
        count++;

        return (
          <ImageCardView
            pathKey={null}
            pathIndex={i}
            card={card}
            favoritesByCards={props.favoritesByCards}
            key={count}
            favoritesStore={props.favoritesStore}
            content={completion}
            insertEditor={props.insertEditor}
            editor={props.editor}
            doneStreaming={doneStreaming}
            onFavorite={props.onFavorite}
            onUnfavorite={props.onUnfavorite}
            onDeleteCardItem={createCardItemDeleteHandler(completion)}
          />
        );
      });

      break;
    case "summary":
      title = (
        <span>
          <p>Summary</p>
        </span>
      );

      prompt = card.data.prompt;

      // never show empty cars
      content = filter(card.data.content).map((completion, i) => {
        if (isContentDeleted(completion)) return null;
        count++;
        return (
          <CardView
            // onRemove={onRemove}
            pathKey={null}
            pathIndex={i}
            card={card}
            favoritesByCards={props.favoritesByCards}
            key={count}
            favoritesStore={props.favoritesStore}
            content={completion}
            insertEditor={props.insertEditor}
            editor={props.editor}
            doneStreaming={doneStreaming}
            onFavorite={props.onFavorite}
            onUnfavorite={props.onUnfavorite}
            onDeleteCardItem={createCardItemDeleteHandler(completion)}
          />
        );
      });

      break;
    case "analyze":
      title = (
        <span>
          <p>Feedback</p>
        </span>
      );

      prompt = card.data.prompt;

      // never show empty cars
      content = filter(card.data.content).map((completion, i) => {
        if (isContentDeleted(completion)) return null;
        count++;
        return (
          <CardView
            // onRemove={onRemove}
            pathKey={null}
            pathIndex={i}
            card={card}
            favoritesByCards={props.favoritesByCards}
            key={count}
            favoritesStore={props.favoritesStore}
            content={completion}
            insertEditor={props.insertEditor}
            editor={props.editor}
            doneStreaming={doneStreaming}
            onFavorite={props.onFavorite}
            onUnfavorite={props.onUnfavorite}
            onDeleteCardItem={createCardItemDeleteHandler(completion)}
          />
        );
      });

      break;
    case "editor":
      title = (
        <span>
          <p>Feedback 2</p>
        </span>
      );

      prompt = card.data.prompt;

      // never show empty cars
      content = filter(card.data.content).map((completion, i) => {
        if (isContentDeleted(completion)) return null;
        count++;
        return (
          <CardView
            // onRemove={onRemove}
            pathKey={null}
            pathIndex={i}
            card={card}
            favoritesByCards={props.favoritesByCards}
            key={count}
            favoritesStore={props.favoritesStore}
            content={completion}
            insertEditor={props.insertEditor}
            doneStreaming={doneStreaming}
            onFavorite={props.onFavorite}
            onUnfavorite={props.onUnfavorite}
            onDeleteCardItem={createCardItemDeleteHandler(completion)}
          />
        );
      });

      break;
    case "expand":
      title = (
        <span>
          <p>Expand</p>
        </span>
      );

      prompt = card.data.prompt;

      // never show empty cars
      content = filter(card.data.content).map((completion, i) => {
        if (isContentDeleted(completion)) return null;
        count++;
        return (
          <CardView
            // onRemove={onRemove}
            pathKey={null}
            pathIndex={i}
            card={card}
            favoritesByCards={props.favoritesByCards}
            key={count}
            favoritesStore={props.favoritesStore}
            content={completion}
            insertEditor={props.insertEditor}
            editor={props.editor}
            doneStreaming={doneStreaming}
            onFavorite={props.onFavorite}
            onUnfavorite={props.onUnfavorite}
            onDeleteCardItem={createCardItemDeleteHandler(completion)}
          />
        );
      });

      break;
    case "command":
    case "quickEditRestore":
      title = (
        <span>
          <p>{card.data.source === "quickEditRestore" ? "Recovered Quick Edit" : "First Draft"}</p>
        </span>
      );

      prompt = card.data.prompt;

      // never show empty cars
      content = filter(card.data.content).map((completion, i) => {
        if (isContentDeleted(completion)) return null;
        count++;
        return (
          <CardView
            // onRemove={onRemove}
            pathKey={null}
            pathIndex={i}
            card={card}
            favoritesByCards={props.favoritesByCards}
            key={count}
            favoritesStore={props.favoritesStore}
            content={completion}
            insertEditor={props.insertEditor}
            editor={props.editor}
            doneStreaming={doneStreaming}
            onFavorite={props.onFavorite}
            onUnfavorite={props.onUnfavorite}
            onDeleteCardItem={createCardItemDeleteHandler(completion)}
          />
        );
      });

      break;

    case "description":
      title = (
        <span>
          <Icon source={card.data.source} />
          <p>Describe</p>
        </span>
      );
      prompt = card.data.object;

      // cards that immediately follow a placeholder card should have a border
      let border = false;

      content = [];

      // construct an object with senses ready to be filled as the it streams
      const senseDisplay = {};
      DESCRIBE_SENSES.map((sense) => {
        if (sense.value !== "metaphor" && (!card.data.senses || card.data.senses.find((s) => s === sense.value))) {
          return {
            key: sense.value,
            value: (
              <>
                <SVG
                  style={{ width: "24px", height: "24px" }}
                  src={sense.image}
                />
                <p>{sense.label}</p>
              </>
            ),
          };
        } else {
          return null;
        }
      })
        .filter((item) => item !== null)
        .forEach((item) => {
          senseDisplay[item.key] = item.value;
        });

      Object.keys(senseDisplay).forEach((sense, i) => {
        let senseContent = card.data.content[sense];
        count++;

        if (senseContent) {
          if (senseContent.length > 0) {
            if (!isContentDeleted(senseContent.join("\n\n"))) {
              content.push(
                <CardView
                  highlightInsert={props.highlightInsert}
                  loading={loading}
                  pathKey={sense}
                  pathIndex={0} // we point to the first sense even though there are multiple (which are concatenated together)
                  favoritesByCards={props.favoritesByCards}
                  card={card}
                  key={count}
                  border={border}
                  favoritesStore={props.favoritesStore}
                  content={senseContent.join("\n\n")}
                  title={senseDisplay[sense]}
                  insertEditor={props.insertEditor}
            editor={props.editor}
                  doneStreaming={doneStreaming}
                  onFavorite={props.onFavorite}
                  onUnfavorite={props.onUnfavorite}
                  hideDivider={card.data.demo}
                  onDeleteCardItem={createCardItemDeleteHandler(senseContent.join("\n\n"))}
                />
              );
              border = false;
            }
          }
        } else if (!doneStreaming) {
          // show a placeholder card if not done streaming and a sense is missing
          content.push(
            <CardLoadingView
              count={i}
              title={senseDisplay[sense]}
              border={border}
            />
          );
          border = true;
        }
      });

      if (card.data.content.metaphor) {
        filter(card.data.content.metaphor).forEach((completion, i) => {
          if (isContentDeleted(completion)) return null;
          count++;
          content.push(
            <CardView
              highlightInsert={props.highlightInsert}
              loading={loading}
              pathKey={"metaphor"}
              pathIndex={i}
              card={card}
              key={count}
              border={border}
              favoritesByCards={props.favoritesByCards}
              favoritesStore={props.favoritesStore}
              content={completion}
              title={
                <>
                  <SVG
                    src="/assets/noun_Rose_1282645.svg"
                    style={{ width: "24px", height: "24px" }}
                  />
                  <p>Metaphor</p>
                </>
              }
              insertEditor={props.insertEditor}
            editor={props.editor}
              doneStreaming={doneStreaming}
              onFavorite={props.onFavorite}
              onUnfavorite={props.onUnfavorite}
              hideDivider={card.data.demo}
              onDeleteCardItem={createCardItemDeleteHandler(completion)}
            />
          );
          border = false;
        });
      }

      break;

    case "twist":
      title = (
        <span>
          <Icon
            top={3}
            width={20}
            source={card.data.source}
          />
          <p>Twist</p>
        </span>
      );

      prompt = card.data.prompt;

      content = filter(card.data.content).map((completion, i) => {
        if (isContentDeleted(completion)) return null;
        count++;
        return (
          <CardView
            pathKey={null}
            pathIndex={i}
            card={card}
            key={count}
            favoritesByCards={props.favoritesByCards}
            favoritesStore={props.favoritesStore}
            content={completion}
            insertEditor={props.insertEditor}
            editor={props.editor}
            doneStreaming={doneStreaming}
            onFavorite={props.onFavorite}
            onUnfavorite={props.onUnfavorite}
            onDeleteCardItem={createCardItemDeleteHandler(completion)}
          />
        );
      });
      break;

    case "character":
      title = (
        <span>
          <Icon
            top={5}
            width={20}
            source={card.data.source}
          />
          <p>Characters</p>
        </span>
      );
      prompt = card.data.prompt;

      let re = /^(.*):(.*)$/;

      content = filter(card.data.content).map((completion, i) => {
        if (isContentDeleted(completion)) return null;
        count++;

        // Get the name
        let matches = re.exec(completion);
        let name, description;

        if (matches && matches[1] && matches[2]) {
          name = <span className="character">{matches[1].trim()}</span>;
          description = matches[2].trim();
        } else {
          name = "Character";
          description = completion;
        }

        return (
          <CardView
            pathKey={null}
            pathIndex={i}
            card={card}
            key={count}
            favoritesByCards={props.favoritesByCards}
            content={description}
            title={name}
            insertEditor={props.insertEditor}
            editor={props.editor}
            doneStreaming={doneStreaming}
            onFavorite={props.onFavorite}
            onUnfavorite={props.onUnfavorite}
            onDeleteCardItem={createCardItemDeleteHandler(completion)}
          />
        );
      });
      break;
    case "brainstorm":
      title = (
        <span>
          <Icon
            height={25}
            width={20}
            source={card.data.source}
          />
          <p>Brainstorm</p>
        </span>
      );

      prompt = card.data.prompt;

      let text = filter(card.data.content)
        .map((item, i) => {
          return `${i + 1}. ${item}`;
        })
        .join("\n\n");

      content = [];

      if (!isContentDeleted(text)) {
        // never show empty cars
        content = [
          <CardView
            // onRemove={onRemove}
            highlightInsert={props.highlightInsert}
            pathKey={null}
            pathIndex={0}
            card={card}
            favoritesByCards={props.favoritesByCards}
            key={0}
            favoritesStore={props.favoritesStore}
            content={text}
            insertEditor={props.insertEditor}
            editor={props.editor}
            doneStreaming={props.doneStreaming}
            onFavorite={props.onFavorite}
            onUnfavorite={props.onUnfavorite}
            disableLikes={true}
            open={props.openBrainstorm}
            onDeleteCardItem={createCardItemDeleteHandlerByIndex(null, 0)}
          />,
        ];
      }

      break;
    case "chapterGenerator":
      title = (
        <span>
          <Icon
            width={25}
            height={25}
            top={0}
            source={card.data.source}
          />
          <p>Draft</p>
        </span>
      );

      prompt = card.data.prompt || card.data.passage || "";

      // SW-9212: skip empty content slots so the output body doesn't render
      // until prose has actually started streaming in (matches Write-card
      // behavior). Title + prompt still show, so the user can see that the
      // Draft is in flight.
      content = card.data.content
        .map((item, originalIndex) => ({ item, originalIndex }))
        .filter(({ item }) => {
          const text = typeof item?.text === "string" ? item.text : item;
          return typeof text === "string" && text.trim().length > 0;
        })
        .slice()
        .reverse()
        .map(({ item, originalIndex }) => {
          count++;
          return (
            <CardView
              // onRemove={onRemove}
              highlightInsert={props.highlightInsert}
              pathKey={null}
              pathIndex={originalIndex}
              card={card}
              favoritesByCards={props.favoritesByCards}
              key={count}
              favoritesStore={props.favoritesStore}
              content={typeof item.text === "string" ? item.text : item}
              title={item.title || null}
              insertEditor={props.insertEditor}
              editor={props.editor}
              doneStreaming={doneStreaming}
              onFavorite={props.onFavorite}
              onUnfavorite={props.onUnfavorite}
              onDeleteCardItem={createCardItemDeleteHandler(typeof item.text === "string" ? item.text : item)}
            />
          );
        });

      // If this filter card set isn't on the top AND we haven't streamed cards in before, then we erase the card
      if (props.position !== 0 && content.length == 0) {
        return null;
      }
      break;
    default:
      if (!card.data.custom) {
        // if not custom then we don't show
        return null;
      }

      // this is a custom function card set

      let func = customFunctions.find((func) => {
        return func.source === card.data.source;
      });

      if (!func) {
        // if custom function not found in the list then we don't show this card set
        return null;
      }

      title = (
        <span>
          <p>{func.name}</p>
        </span>
      );

      prompt = card.data.prompt;

      // never show empty cars
      content = filter(card.data.content).map((completion, i) => {
        count++;
        return (
          <CardView
            highlightInsert={props.highlightInsert}
            pathKey={null}
            pathIndex={i}
            card={card}
            favoritesByCards={props.favoritesByCards}
            key={count}
            favoritesStore={props.favoritesStore}
            content={completion}
            insertEditor={props.insertEditor}
            editor={props.editor}
            doneStreaming={doneStreaming}
            onFavorite={props.onFavorite}
            onUnfavorite={props.onUnfavorite}
            onDeleteCardItem={createCardItemDeleteHandler(completion)}
          />
        );
      });

      break;
  }

  let inner, header, cards, input;

  if (prompt === undefined) {
    throw new Error(`No prompt found for ${card.data.id}`);
  }

  if (prompt === null) prompt = "";

  const truncateLength = 60;
  let needsTruncation = false;
  let collapsedPrompt = prompt;

  if (prompt.length > truncateLength) {
    needsTruncation = true;

    collapsedPrompt = collapsedPrompt.slice(prompt.length - truncateLength, prompt.length);
    collapsedPrompt = "... " + collapsedPrompt;

    if (truncatePrompt) {
      prompt = prompt.slice(prompt.length - truncateLength, prompt.length);
      prompt = "... " + prompt;
    }
  }

  if (loading) {
    // LOADING
    header = (
      <div className="loading">
        <div className="under-1"></div>
        <div className="card-title">{title}</div>
        <div className="prompt">{collapsedPrompt}</div>

        {errorMessage}
      </div>
    );
  } else if (showCards) {
    const chiclets = Object.keys(cardChiclets).length > 0 ? cardChiclets : (card.data?.chiclets || {});

    // Check if there are any non-language, non-model chiclets with actual content
    const hasNonLanguageChiclets = Object.keys(chiclets).some((key) => {
      if (key === "language" || key === "model") return false;

      // current and previous document chiclets are currently restricted to users in the chapter glue beta
      // we should remove this restriction once the beta is fully launched
      if (key === "currentDocument" || key === "previousDocument") {
        return subscription?.hasAccessToChapterGlueBeta;
      }

      // For array types (characters, worldbuilding)
      if (Array.isArray(chiclets[key])) {
        return chiclets[key].length > 0;
      }

      // For string types (outline_item)
      if (typeof chiclets[key] === "string") {
        return chiclets[key].trim() !== "";
      }

      // For other types (booleans, numbers)
      return Boolean(chiclets[key]);
    });
    // Cards are expanded
    header = (
      <div
        className="header"
        href="#"
        onClick={(e) => {
          e.preventDefault();

          // Don't close the card if the user is trying to copy
          // text from it
          const activeSelection = typeof window !== "undefined" ? window.getSelection() : null;
          if (activeSelection && activeSelection.toString().length > 0) {
            return;
          }

          setHidden(true);
        }}
      >
        <div className="card-title">{title}</div>
        {errorMessage && <div className="pt-1 pb-2.5 px-3.5">{errorMessage}</div>}
        {prompt && (
          <div className="prompt">
            {prompt}
            {needsTruncation && (
              <span>
                {" "}
                (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setTruncatePrompt(!truncatePrompt);
                  }}
                >
                  {truncatePrompt ? "show more" : "show less"}
                </a>
                )
              </span>
            )}
          </div>
        )}
        {hasNonLanguageChiclets && !isEmpty(chiclets) && (
          <div className="chiclets">
            <p>Looked at: </p>
            {[
              ...(chiclets.characters || []).map((character, index) => (
                <Chiclet
                  key={`character-${index}`}
                  label={character}
                  src="/assets/story-bible/characters-icon.svg"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    elementRefs?.characters?.current?.scrollIntoView({ behavior: "smooth" });
                  }}
                />
              )),
              ...(chiclets.worldbuilding || []).map((entry, index) => (
                <Chiclet
                  key={`worldbuilding-${index}`}
                  label={entry}
                  src="/assets/story-bible/worldbuilding-icon.svg"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    elementRefs?.worldbuilding?.current?.scrollIntoView({ behavior: "smooth" });
                  }}
                />
              )),
              typeof chiclets.scene_summary === "number" && (
                <Chiclet
                  key="scene-summary"
                  label={`Outline: Chapter ${chiclets.scene_summary}`}
                  src="/assets/story-bible/outline-icon.svg"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    elementRefs?.outline?.current?.scrollIntoView({ behavior: "smooth" });
                  }}
                />
              ),
              chiclets.outline_item && (
                <Chiclet
                  key="outline-item"
                  label={`Outline: ${chiclets.outline_item}`}
                  src="/assets/story-bible/outline-icon.svg"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    elementRefs?.outline?.current?.scrollIntoView({ behavior: "smooth" });
                  }}
                />
              ),
              chiclets.genre && (
                <Chiclet
                  key="genre"
                  label={"Genre"}
                  src="/assets/story-bible/genre-icon.svg"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    elementRefs?.genre?.current?.scrollIntoView({ behavior: "smooth" });
                  }}
                />
              ),
              chiclets.synopsis && (
                <Chiclet
                  key="synopsis"
                  label={"Synopsis"}
                  src="/assets/story-bible/synopsis-icon.svg"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    elementRefs?.synopsis?.current?.scrollIntoView({ behavior: "smooth" });
                  }}
                />
              ),
              chiclets.style && (
                <Chiclet
                  key="style"
                  label={getStyleChicletLabel(chiclets.style)}
                  src="/assets/story-bible/style-icon.svg"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    elementRefs?.style?.current?.scrollIntoView({ behavior: "smooth" });
                  }}
                />
              ),
              chiclets.currentDocument && subscription?.hasAccessToChapterGlueBeta && (
                <Chiclet
                  key="current-document"
                  label={`${chiclets.currentDocument.title} (${Utils.numberWithCommas(chiclets.currentDocument.wordCount)} words)`}
                  src="/assets/document.svg"
                  onClick={() => {}} // no-op
                />
              ),
              ...(chiclets.previousDocuments && chiclets.previousDocuments.length > 0 && subscription?.hasAccessToChapterGlueBeta
                ? chiclets.previousDocuments.map((doc) => (
                    <Chiclet
                      key={`prev-doc-${doc.id}`}
                      label={`${doc.title} (${Utils.numberWithCommas(doc.wordCount)} words)`}
                      src="/assets/document.svg"
                      onClick={() => {}} // no-op, eventually we can open the doc in a new tab or something
                    />
                  ))
                : []),
              chiclets.pov_value && !isAutoPOV(chiclets.pov_value) && (
                <Chiclet
                  key="pov-value"
                  label={`${chiclets.pov_value}`}
                  src="/assets/pov-tense.svg"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isActive) {
                      setIsActive(true);
                      setTimeout(() => {
                        scrollToOutline();
                      }, 600);
                    } else {
                      scrollToOutline();
                    }
                  }}
                />
              ),
              chiclets.tense_value && !isAutoPOV(chiclets.tense_value) && (
                <Chiclet
                  key="tense-value"
                  label={`${chiclets.tense_value.charAt(0).toUpperCase() + chiclets.tense_value.slice(1)} tense`}
                  src="/assets/pov-tense.svg"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isActive) {
                      setIsActive(true);
                      setTimeout(() => {
                        scrollToOutline();
                      }, 600);
                    } else {
                      scrollToOutline();
                    }
                  }}
                />
              ),
              chiclets.pov_characters && (
                <Chiclet
                  key="pov-characters"
                  label={`POV Characters: ${chiclets.pov_characters}`}
                  src="/assets/pov-tense.svg"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isActive) {
                      setIsActive(true);
                      setTimeout(() => {
                        scrollToOutline();
                      }, 600);
                    } else {
                      scrollToOutline();
                    }
                  }}
                />
              ),
            ].filter(Boolean)}
          </div>
        )}
        {chiclets.language && (
          <div className="chiclets">
            <p>Language: </p>
            <Chiclet
              label={`${chiclets.language}`}
              src="/assets/language.svg"
            />
          </div>
        )}
        {chiclets.model && (
          <div className="chiclets">
            <p>Model: </p>
            <Chiclet
              label={chiclets.model}
              src="/assets/model.svg"
            />
          </div>
        )}
      </div>
    );
  } else {
    // HIDDEN / COLLAPSED
    header = (
      <div
        href="#"
        className="expand"
        onClick={(e) => {
          e.preventDefault();
          setHidden(!hidden);
          //setAnimationState(true);
        }}
      >
        <div className="collapsed">
          <div className="under-1"></div>
          <div className="card-title">{title}</div>
          <div className="prompt">{collapsedPrompt}</div>
        </div>
      </div>
    );
  }

  // Individual Cards
  if (!loading) {
    cards = (
      <div className="cards">
        <animated.div style={{ ...expand, height: "auto" }}>
          <div ref={ref}>
            {content.map((c, i) => {
              // return(
              //   <animated.div key={i} style={aprops}>
              //     {c}
              //   </animated.div>
              // )

              if (i == 0) {
                return firstCardTransitions.map(({ item, key, props }) => {
                  return (
                    item && (
                      <animated.div
                        key={key}
                        style={props}
                      >
                        {c}
                      </animated.div>
                    )
                  );
                });
              } else {
                return cardTransitions.map(({ item, key, props }) => {
                  return (
                    item && (
                      <animated.div
                        key={key}
                        style={props}
                      >
                        {c}
                      </animated.div>
                    )
                  );
                });
              }
            })}
          </div>
        </animated.div>
      </div>
    );
  }

  const hasVisibleContent = Array.isArray(content) && content.some(Boolean);
  const showRewriteInput = card.data.source === "rewrite" && showCards && props.position === 0 && (!doneStreaming || !hasVisibleContent);

  inner = (
    <>
      <div className="card-set">
        {header}

        {showRewriteInput && (
          <FilterInput
            initiateNewFilter={(modifier) => {
              setHidden(true);
              props.rememberModifier?.(modifier);
              props.openFilter(null, modifier);
            }}
            numberCards={content.length}
            getSelection={props.getSelection}
            prompt={card.data.prompt}
            disabled={!doneStreaming}
            modifier={card.data.modifier}
            setPrompt={(p) => {
              card.data.prompt = p;
            }}
            submit={(modifier) => {
              // Kick off the request
              setReady(true);
              setDoneStreaming(false);
              setParams({
                modifier: modifier,
                prompt: card.data.prompt,
                documentId: card.data.documentId,
                outputCards: card.data.outputCards,
              });
              props.rememberModifier?.(modifier);
            }}
          />
        )}
        {cards}
      </div>
    </>
  );

  if (props.onVisible) {
    return (
      <InView
        as="div"
        onChange={(inView, entry) => {
          if (inView) props.onVisible();
        }}
      >
        {inner}
      </InView>
    );
  } else {
    return inner;
  }
}

// A placeholder card
function CardLoadingView(props) {
  return (
    <div
      key={props.count}
      className={classnames({ "card-loading": true, border: props.border })}
    >
      <p className="title">{props.title}</p>
    </div>
  );
}
